import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { addToCart, removeFromCart, incrementQuantity, decrementQuantity } from "./CartReducer";
import Headercomponent from "./Headercomponent";
import {  Snackbar } from "@mui/material"; // Import Snackbar and Button

const ProductDetailScreen = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { product } = location.state || {}; // Default to empty object if state is not present
  const cart = useSelector((state) => state.cart.cart); // Access the cart from Redux store

  // Snackbar state
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Fallback if product is missing
  if (!product) {
    return <div>Product not found</div>;
  }

  // Get the quantity of the product in the cart
  const getItemCount = (product) => {
    const items = cart || []; // Fallback to empty array if cart is undefined
    const item = items.find((item) => item.ID === product.ID);
    return item ? item.quantity : 0; // Return quantity if item exists
  };

  const quantity = getItemCount(product);

  // Format the price to include commas
  const formatPrice = (price) => {
    if (price == null || isNaN(price)) {
      return "0.00"; // Default to 0.00 if price is invalid
    }
    return parseFloat(price)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Show Snackbar message
  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setOpenSnackbar(true);
    setTimeout(() => {
      setOpenSnackbar(false); // Close the snackbar after 3 seconds
    }, 3000);
  };

  // Handle cart updates
  const handleCartAction = (action) => {
    switch (action) {
      case "add":
        dispatch(addToCart(product)); // Add product to the cart
        showSnackbar(`You now have 1 ${product.ID} in the cart.`);
        break;
      case "increment":
        dispatch(incrementQuantity(product)); // Increment the product quantity in the cart
        showSnackbar(`You now have ${quantity + 1} ${product.ID} in the cart`);
        break;
      case "decrement":
        if (quantity > 1) {
          dispatch(decrementQuantity(product)); // Decrement the product quantity in the cart
          showSnackbar(`You now have ${quantity - 1} ${product.ID} in the cart.`);
        } else {
          dispatch(removeFromCart(product)); // Remove the product from the cart if quantity is 0
          showSnackbar(`${product.ID} is removed from the cart.`);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div style={styles.container}>
      {/* Header Component */}
      <div style={styles.headerContainer}>
        <Headercomponent />
      </div>

      {/* Product Details */}
      <h1 style={styles.productName}>{product.ID}</h1>

      <div style={styles.imageContainer}>
        <img
          src={product.image}
          alt={product.ID}
          style={styles.productImage}
        />
      </div>

      <div style={styles.descriptionContainer}>
        <p style={styles.productDescription}>
          {product.description || "No description available."}
        </p>
      </div>

      <div style={styles.priceContainer}>
        <h2 style={styles.productPrice}>
          {product.Price !== undefined && product.Price !== null
            ? `₦${formatPrice(product.Price)}`
            : "Price Not Available"}
        </h2>
      </div>

      {/* Add to Cart / Increment / Decrement Button Logic */}
      <div style={styles.buttonContainer}>
        {quantity === 0 ? (
          <button
            style={styles.addToCartButton}
            onClick={() => handleCartAction("add")}
          >
            Add to Cart
          </button>
        ) : (
          <div style={styles.quantityControlContainer}>
            <button
              style={styles.quantityButton}
              onClick={() => handleCartAction("decrement")}
            >
              -
            </button>
            <span style={styles.quantityDisplay}>{quantity}</span>
            <button
              style={styles.quantityButton}
              onClick={() => handleCartAction("increment")}
            >
              +
            </button>
          </div>
        )}
      </div>

      {/* Snackbar for alert */}
      <Snackbar
        open={openSnackbar}
        message={snackbarMessage}
        autoHideDuration={3000} // Duration before auto-closing
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          style: {
            backgroundColor: "#000", // Black background
            color: "#fff", // White text
            fontSize: "16px", // Slightly smaller font
            padding: "15px 20px", // Padding for better visual appeal
            textAlign: "center",
          },
        }}
      />
    </div>
  );
};

// Styling for the components
const styles = {
  container: {
    padding: "0px",
    maxWidth: "800px",
    margin: "0 auto",
    textAlign: "center",
    position: "relative",
  },
  headerContainer: {
    marginBottom: "20px",
  },
  productName: {
    fontSize: "24px",
    fontWeight: "bold",
    textAlign: "center",
  },
  imageContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "20px 0",
  },
  productImage: {
    width: "100%",
    maxWidth: "300px",
    height: "300px",
    borderRadius: "10px",
  },
  descriptionContainer: {
    textAlign: "center",
    marginBottom: "20px",
  },
  productDescription: {
    fontSize: "16px",
    color: "#555",
  },
  priceContainer: {
    textAlign: "center",
    marginBottom: "40px", // Increased spacing below the price
  },
  productPrice: {
    fontSize: "28px",
    color: "#333",
    fontWeight: "600",
  },
  buttonContainer: {
    marginTop: "40px", // Adds more spacing between the price and buttons
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  addToCartButton: {
    padding: "10px 20px",
    fontSize: "16px",
    backgroundColor: "#4CAF50",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  },
  quantityControlContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  quantityButton: {
    padding: "10px 20px",
    fontSize: "18px",
    backgroundColor: "#4CAF50",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
    width: "50px",
  },
  quantityDisplay: {
    margin: "0 15px",
    fontSize: "18px",
    fontWeight: "600",
  },
};

export default ProductDetailScreen;
